// @ts-strict-ignore
import { createBrowserHistory as createHistory } from 'history';
import hypernova, { load, serialize } from 'hypernova';
import queryString from 'query-string';
import React, { ReactNode, createElement, useEffect } from 'react';
import { hydrate } from 'react-dom';
import { renderToStaticMarkup, renderToString } from 'react-dom/server';
import Loadable from 'react-loadable';
import { Provider } from 'react-redux';
import { Router, StaticRouter, useLocation } from 'react-router-dom';
import { Store } from 'redux';
import { ServerStyleSheet } from 'styled-components';

import { CurrentUser } from 'api/currentUser';
import { WithConfig } from 'containers/WithConfig';
import { MainStoreState } from 'store/main';
import {
  setABExperimentBucket,
  updateCurrentUser,
} from 'store/main/ducks/currentUser';
import { updateSearchLocation } from 'store/main/ducks/mainSearch';
import { StaffStoreState } from 'store/staff';
import {
  bootstrapAnalytics,
  registerAbExperiments,
  trackEvent,
} from 'utils/analytics';
import configureTrackJs from 'utils/configureTrackJs';
import { initXMLHttpRequestActiveCounter } from 'utils/http';
import { renderAmpPage } from 'utils/renderAmpPage';
import { getInitialSearchLocation } from 'utils/search/getInitialSearchLocation';
import { trackReferrals } from 'utils/trackReferral';

const performanceMark = (...args) => {
  if (
    window &&
    window.performance &&
    typeof window.performance.mark === 'function'
  ) {
    // @ts-expect-error ts-migrate(2556) FIXME: Expected 1 arguments, but got 0 or more.
    window.performance.mark(...args);
  }
};

function AppContainer({ children }: { children: ReactNode }) {
  const location = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
}

// Accept a query param to override an AB experiment bucket:
// ?ab_test[button_color]=red
const overrideABExperiments = (store, currentUser: CurrentUser) => {
  const queryParams = queryString.parse(window.location.search);
  const overrides = Object.keys(queryParams).reduce((acc, param) => {
    const matches = param.match(/^ab_test\[([^\]]+)\]$/);

    if (matches) {
      acc.push({
        id: matches[1],
        bucket: queryParams[param],
      });
    }

    return acc;
  }, []);
  overrides.forEach(({ id, bucket }) => {
    const experiment = currentUser.abExperiments.find((e) => e.id === id);

    if (!experiment || experiment.bucket !== bucket) {
      store.dispatch(setABExperimentBucket(id, bucket));
    }
  });
};

function initUserPromise(store) {
  // Instead of fetching the user here, we're making the request in the
  // HTML template directly. Otherwise, the response from the Link HTTP header is
  // not used and we're making a second request.
  const userPromise = new Promise((resolve) => {
    if (window.Idealist.initUser) {
      // We're using setTimeout here because just resolving here introduced
      // some strange issues. Maybe we could get rid of it at some point.
      setTimeout(() => resolve(window.Idealist.initUser), 1);
    } else {
      // in some cases, the user is in the state from Hypernova
      const { currentUser } = store.getState();
      if (currentUser && currentUser.isLoaded) {
        resolve(currentUser);
      }
      const interval = setInterval(() => {
        if (window.Idealist.initUser) {
          resolve(window.Idealist.initUser);
          clearInterval(interval);
        }
      }, 50);
    }
  });
  return userPromise;
}

const login = (store): Promise<CurrentUser> => {
  const { searchLocation } = store.getState().mainSearch;
  return initUserPromise(store).then((currentUser: CurrentUser) => {
    store.dispatch(updateCurrentUser(currentUser));
    if (!searchLocation) {
      store.dispatch(
        updateSearchLocation(
          getInitialSearchLocation(currentUser.guessedLocation),
          false
        )
      );
    }

    return currentUser;
  });
};

const renderReact = (
  name: string,
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: React.ComponentType<any>,
  createStore: (preloadedState?: MainStoreState | StaffStoreState) => Store
) =>
  hypernova({
    server() {
      return Loadable.preloadAll().then(() => (props) => {
        const componentWrapper = ({
          url,
          state,
          requestIsAmp,
          config,
        }: {
          url: string;
          state: MainStoreState | StaffStoreState;
          requestIsAmp: boolean;
          // TODO: Fix this the next time the file is edited.
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          config: Record<string, any>;
        }) => {
          const store = createStore(state);
          const slug = url
            .substring(1, url.length)
            .split('?')[0]
            .replace(/\/$/, '');

          const content =
            state &&
            state.contents &&
            state.contents.byUrl &&
            state.contents.byUrl[slug];

          return (
            <Provider store={store}>
              <WithConfig config={config}>
                <StaticRouter location={url} context={{}}>
                  <Component requestIsAmp={requestIsAmp} content={content} />
                </StaticRouter>
              </WithConfig>
            </Provider>
          );
        };

        const sheet = new ServerStyleSheet();
        const element = createElement(componentWrapper, props);

        if (props.requestIsAmp) {
          const {
            title,
            description,
            canonicalUrl,
            altUrls,
            ampTrackingId,
            ampSchemaMarkup,
          } = props;
          const body = renderToStaticMarkup(
            sheet.collectStyles(element)
          ).replace(/data-style/g, 'style');
          const styleTags = sheet
            .getStyleTags()
            .replace(/<\/?style[^>]*>/g, '')
            .replace(/@media screen and \(min-width:(576|768|992)px\){}/g, '');
          return renderAmpPage({
            title,
            description,
            styles: styleTags,
            body,
            canonicalUrl,
            altUrls,
            ampTrackingId,
            ampSchemaMarkup,
          });
        }

        const html = renderToString(sheet.collectStyles(element));
        const styleTags = sheet
          .getStyleTags()
          .replace(/@media screen and \(min-width:(576|768|992)px\){}/g, '');
        const markup = serialize(name, html, {
          config: props.config,
          state: props.state,
        });
        return `${styleTags}\n${markup}`;
      });
    },

    client() {
      performanceMark('Starting app');
      const browserHistory = createHistory();
      // Track js "pageViews" using GTM
      browserHistory.listen((location, action) => {
        if (action !== 'REPLACE') {
          trackEvent('JS Page View');
        }
      });

      // @ts-expect-error ts-migrate(2339) FIXME: Property 'debugXhr' does not exist on type '{ page... Remove this comment to see the full error message
      if (window.Idealist && window.Idealist.debugXhr) {
        initXMLHttpRequestActiveCounter();
      }

      const payloads = load(name);

      if (payloads.length > 0) {
        const payload = payloads[0];
        const {
          node,
          data: { state, config },
        } = payload;

        if (node) {
          performanceMark('Before waiting for loadables');
          Loadable.preloadReady().then(() => {
            performanceMark('Done waiting for loadables');
            const store = createStore(state);
            const slug = window.location.pathname
              .substring(1, window.location.pathname.length)
              .split('?')[0]
              .replace(/\/$/, '');
            const content =
              state &&
              state.contents &&
              state.contents.byUrl &&
              state.contents.byUrl[slug];
            performanceMark('Before setting login state');
            login(store).then((currentUser) => {
              performanceMark('Done setting login state');
              configureTrackJs(currentUser.id);
              bootstrapAnalytics({
                user: currentUser,
              });
              trackReferrals();
              store.subscribe(() => {
                registerAbExperiments(
                  store.getState().currentUser.abExperiments
                );
              });
              overrideABExperiments(store, currentUser);
              performanceMark('User data first load');
            });
            performanceMark('Starting hydration');
            hydrate(
              <Provider store={store}>
                <WithConfig config={config}>
                  <Router history={browserHistory}>
                    <AppContainer>
                      <Component content={content} />
                    </AppContainer>
                  </Router>
                </WithConfig>
              </Provider>,
              node,
              () => performanceMark('App ready')
            );
          });
        }
      }
    },
  });

// eslint-disable-next-line import/no-default-export
export default renderReact;
